import React, { useState, useEffect, useRef, Component, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { 
  ArrowLeft, 
  Upload, 
  FolderUp, 
  Check, 
  X, 
  AlertCircle, 
  Image as ImageIcon,
  User,
  Users,
  Search,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ChevronsUpDown,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, postWithRetry } from '../../lib/uploadUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error Boundary to catch crashes
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Photo Upload Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-2xl mx-auto p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Something went wrong</h2>
          <p className="text-slate-600 mb-4">
            There was an error processing your photos. This might happen with very large folders.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Try uploading fewer files at once, or use &quot;Select Files&quot; to pick individual photos.
          </p>
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload Page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Searchable Combobox for selecting preorders
const PreorderCombobox = ({ value, onValueChange, preordersByTeam, placeholder = "Select child...", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Find current selection
  const selectedPreorder = value ? 
    Object.values(preordersByTeam).flat().find(p => p.id === value) : null;
  
  // Filter preorders based on search
  const filteredTeams = useMemo(() => {
    if (!searchQuery) return preordersByTeam;
    
    const query = searchQuery.toLowerCase();
    const filtered = {};
    
    for (const [team, preorders] of Object.entries(preordersByTeam)) {
      const matchingPreorders = preorders.filter(p => 
        (p.child_name || '').toLowerCase().includes(query) ||
        (p.team_name || '').toLowerCase().includes(query)
      );
      if (matchingPreorders.length > 0) {
        filtered[team] = matchingPreorders;
      }
    }
    
    return filtered;
  }, [preordersByTeam, searchQuery]);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`justify-between h-8 text-xs font-normal ${className}`}
        >
          <span className="truncate">
            {selectedPreorder 
              ? `${selectedPreorder.child_name} (${selectedPreorder.team_name || 'No team'})`
              : placeholder
            }
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search by name or team..." 
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList className="max-h-[250px]">
            <CommandEmpty>No child found.</CommandEmpty>
            {value && (
              <CommandGroup>
                <CommandItem
                  value="__unassign__"
                  onSelect={() => {
                    onValueChange('__unassign__');
                    setOpen(false);
                    setSearchQuery('');
                  }}
                  className="text-orange-600"
                >
                  <X className="mr-2 h-3 w-3" />
                  Unassign
                </CommandItem>
              </CommandGroup>
            )}
            {Object.entries(filteredTeams).map(([team, teamPreorders]) => (
              <CommandGroup key={team} heading={team}>
                {teamPreorders.map((preorder) => (
                  <CommandItem
                    key={preorder.id}
                    value={preorder.id}
                    onSelect={() => {
                      onValueChange(preorder.id);
                      setOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    <Check
                      className={`mr-2 h-3 w-3 ${value === preorder.id ? 'opacity-100' : 'opacity-0'}`}
                    />
                    {preorder.child_name}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const EventPhotoUploadPageInner = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const folderInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);
  
  const [event, setEvent] = useState(null);
  const [preorders, setPreorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  // Photo matching state
  const [isDragging, setIsDragging] = useState(false);
  const [photoMatches, setPhotoMatches] = useState([]); // { file, childName, matchedPreorder, confidence }
  const [teamPhotoMatches, setTeamPhotoMatches] = useState([]); // { file, teamName, matchedPreorders: [] }
  const [unmatchedPhotos, setUnmatchedPhotos] = useState([]);
  const [step, setStep] = useState('upload'); // 'upload', 'review', 'complete'
  const [assigningAll, setAssigningAll] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [processingFiles, setProcessingFiles] = useState(false);
  const [managedTeamPhotos, setManagedTeamPhotos] = useState(null); // null = not loaded
  const [loadingTeamPhotos, setLoadingTeamPhotos] = useState(false);
  const [removingTeamPhoto, setRemovingTeamPhoto] = useState(null);

  const removeFromTeamMatch = (matchIndex, preorderId) => {
    setTeamPhotoMatches(prev => prev.map((m, i) =>
      i === matchIndex ? { ...m, matchedPreorders: m.matchedPreorders.filter(p => p.id !== preorderId) } : m
    ));
  };

  const addToTeamMatch = (matchIndex, preorderId) => {
    const preorder = preorders.find(p => p.id === preorderId);
    if (!preorder) return;
    setTeamPhotoMatches(prev => prev.map((m, i) =>
      i === matchIndex && !m.matchedPreorders.some(p => p.id === preorderId)
        ? { ...m, matchedPreorders: [...m.matchedPreorders, preorder] }
        : m
    ));
  };

  const fetchTeamPhotoManager = async () => {
    setLoadingTeamPhotos(true);
    try {
      const res = await axios.get(`${API}/preorder-events/${eventId}/team-photos`);
      setManagedTeamPhotos(res.data.team_photos);
    } catch (error) {
      toast.error('Failed to load uploaded team photos');
    }
    setLoadingTeamPhotos(false);
  };

  const removeTeamPhotoAssignment = async (photoId, teamNames, label) => {
    if (!window.confirm(`Remove this team photo from ${label}? The photo stays in the gallery - only the assignments are removed.`)) return;
    setRemovingTeamPhoto(photoId);
    try {
      const res = await axios.post(`${API}/preorder-events/${eventId}/remove-team-photo`, {
        photo_id: photoId,
        team_names: teamNames
      });
      toast.success(`Removed from ${res.data.removed_from} pre-order${res.data.removed_from === 1 ? '' : 's'}`);
      await fetchTeamPhotoManager();
      // refresh preorders so match lists reflect the change
      const poRes = await axios.get(`${API}/preorders?event_id=${eventId}`);
      setPreorders(poRes.data.preorders || poRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team photo');
    }
    setRemovingTeamPhoto(null);
  };

  // Sorted preorders grouped by team for the dropdown
  const sortedPreorders = useMemo(() => {
    if (!preorders || preorders.length === 0) return [];
    
    // Sort by team name first, then by child name
    return [...preorders].sort((a, b) => {
      const teamA = (a.team_name || 'ZZZ No Team').toLowerCase();
      const teamB = (b.team_name || 'ZZZ No Team').toLowerCase();
      if (teamA !== teamB) return teamA.localeCompare(teamB);
      
      const nameA = (a.child_name || '').toLowerCase();
      const nameB = (b.child_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [preorders]);

  // Group preorders by team for the dropdown display
  const preordersByTeam = useMemo(() => {
    if (!sortedPreorders || sortedPreorders.length === 0) return {};
    
    return sortedPreorders.reduce((acc, preorder) => {
      const team = preorder.team_name || 'No Team';
      if (!acc[team]) acc[team] = [];
      acc[team].push(preorder);
      return acc;
    }, {});
  }, [sortedPreorders]);

  useEffect(() => {
    fetchEventAndPreorders();
  }, [eventId]);

  const fetchEventAndPreorders = async () => {
    try {
      const eventRes = await axios.get(`${API}/preorder-events/${eventId}`);
      setEvent(eventRes.data);
      
      // First try to get preorders for this specific event
      let preordersRes = await axios.get(`${API}/preorders?event_id=${eventId}`);
      let preordersData = preordersRes.data.preorders || preordersRes.data;
      
      
      // If very few preorders, also fetch ALL preorders as fallback
      // (in case event_id wasn't set correctly on some preorders)
      if (preordersData.length < 50) {
        const allPreordersRes = await axios.get(`${API}/preorders`);
        const allPreorders = allPreordersRes.data.preorders || allPreordersRes.data;
        
        // Use all preorders if significantly more are available
        if (allPreorders.length > preordersData.length * 2) {
          preordersData = allPreorders;
          toast.info(`Using all ${allPreorders.length} preorders for matching (event filter returned only ${preordersRes.data.length || 0})`);
        }
      }
      
      if (preordersData.length > 0) {
      }
      
      setPreorders(preordersData);
    } catch (error) {
      console.error('Failed to load event/preorders:', error);
      toast.error('Failed to load event data');
      navigate('/admin/preorder-events');
    } finally {
      setLoading(false);
    }
  };

  // Extract child name from filename
  // Handles formats like: "Dalton Fletcher-01.jpg", "Dalton_Fletcher_01.jpg", "Fletcher, Dalton - 01.jpg"
  const extractChildName = (filename) => {
    // Remove file extension
    let name = filename.replace(/\.[^/.]+$/, '');
    
    // Remove common suffixes like -01, _01, -1, _1, - 01, _ 01, etc.
    name = name.replace(/[-_\s]*\d+$/, '');
    
    // Replace underscores and hyphens with spaces
    name = name.replace(/[_-]/g, ' ');
    
    // Handle "Last, First" format
    if (name.includes(',')) {
      const parts = name.split(',').map(p => p.trim());
      if (parts.length === 2) {
        name = `${parts[1]} ${parts[0]}`;
      }
    }
    
    // Clean up extra spaces and trim
    name = name.replace(/\s+/g, ' ').trim();
    
    return name;
  };

  // Normalize a name for comparison (remove special chars, extra spaces, lowercase)
  const normalizeName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Check if file is a team photo and extract team name
  // Handles formats like: "Glitter Goals - Team Photo.jpg", "Team Photo - Glitter Goals.jpg"
  const extractTeamPhotoInfo = (filename) => {
    const name = filename.replace(/\.[^/.]+$/, '').trim();
    const lowerName = name.toLowerCase();
    
    // Check if it's a team photo
    if (lowerName.includes('team photo') || lowerName.includes('team-photo') || lowerName.includes('team_photo')) {
      // Extract team name - try different patterns
      let teamName = name;
      
      // Pattern: "Team Name - Team Photo" or "Team Name Team Photo"
      teamName = teamName.replace(/[-_\s]*(team[-_\s]*photo)[-_\s]*/i, '').trim();
      
      // Clean up
      teamName = teamName.replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();
      
      return { isTeamPhoto: true, teamName };
    }
    
    return { isTeamPhoto: false, teamName: null };
  };

  // Find all preorders matching a team name
  const findPreordersByTeam = (teamName) => {
    if (!teamName || !preorders || preorders.length === 0) return [];
    
    const normalizedTeam = teamName.toLowerCase().trim();
    
    // Exact team-name matches take priority (prevents cross-division mixups for similar names)
    const exactMatches = preorders.filter(p => (p.team_name || '').toLowerCase().trim() === normalizedTeam);
    if (exactMatches.length > 0) return exactMatches;
    
    return preorders.filter(p => {
      const preorderTeam = (p.team_name || '').toLowerCase().trim();
      if (!preorderTeam) return false;
      
      // Exact match
      if (normalizedTeam === preorderTeam) return true;
      
      // One contains the other
      if (normalizedTeam.includes(preorderTeam) || preorderTeam.includes(normalizedTeam)) return true;
      
      // Word matching for partial matches
      const teamWords = normalizedTeam.split(' ');
      const preorderWords = preorderTeam.split(' ');
      const matchedWords = teamWords.filter(w => preorderWords.some(pw => pw.includes(w) || w.includes(pw)));
      
      return matchedWords.length >= Math.min(teamWords.length, preorderWords.length);
    });
  };

  // Fuzzy match child name to preorders
  const findMatchingPreorder = (childName) => {
    if (!childName || !preorders || preorders.length === 0) return { preorder: null, confidence: 0 };
    
    const normalizedInput = normalizeName(childName);
    if (!normalizedInput) return { preorder: null, confidence: 0 };
    
    // Debug: Check for specific problematic names
    const isDebugName = childName.toLowerCase().includes('reilly') || 
                        childName.toLowerCase().includes('parker') ||
                        childName.toLowerCase().includes('hofer') ||
                        childName.toLowerCase().includes('hackett');
    
    if (isDebugName) {
    }
    
    let bestMatch = null;
    let bestScore = 0;
    let exactMatchFound = null;
    
    for (const preorder of preorders) {
      const preorderName = normalizeName(preorder.child_name);
      if (!preorderName) continue;
      
      // Debug: Check if this is the expected match
      if (isDebugName && (preorder.child_name.toLowerCase().includes('reilly') || 
                          preorder.child_name.toLowerCase().includes('parker') ||
                          preorder.child_name.toLowerCase().includes('hofer') ||
                          preorder.child_name.toLowerCase().includes('hackett'))) {
      }
      
      // Exact match (normalized)
      if (normalizedInput === preorderName) {
        if (isDebugName) {
        }
        return { preorder, confidence: 100 };
      }
      
      // Check if one contains the other completely
      if (normalizedInput.includes(preorderName) || preorderName.includes(normalizedInput)) {
        const score = 95;
        if (score > bestScore) {
          bestScore = score;
          bestMatch = preorder;
        }
        continue;
      }
      
      // Compare words - improved algorithm
      const inputWords = normalizedInput.split(' ').filter(w => w.length > 0);
      const preorderWords = preorderName.split(' ').filter(w => w.length > 0);
      
      let matchedWords = 0;
      let partialMatches = 0;
      
      for (const inputWord of inputWords) {
        for (const preorderWord of preorderWords) {
          if (inputWord === preorderWord) {
            matchedWords++;
            break;
          } else if (inputWord.length >= 3 && preorderWord.length >= 3) {
            // Partial word matching for typos (at least 3 chars)
            if (inputWord.includes(preorderWord) || preorderWord.includes(inputWord)) {
              partialMatches++;
              break;
            }
          }
        }
      }
      
      // Score based on word matches
      const totalWords = Math.max(inputWords.length, preorderWords.length);
      const fullMatchScore = (matchedWords / totalWords) * 85;
      const partialMatchScore = (partialMatches / totalWords) * 40;
      const score = Math.min(fullMatchScore + partialMatchScore, 95);
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = preorder;
      }
    }
    
    if (isDebugName) {
    }
    
    return { 
      preorder: bestScore >= 50 ? bestMatch : null, 
      confidence: Math.round(bestScore) 
    };
  };

  const handleFiles = async (files, appendMode = false) => {
    setProcessingFiles(true);
    
    // Use setTimeout to allow UI to update before heavy processing
    setTimeout(async () => {
      try {
        const fileArray = Array.from(files);
        const imageFiles = fileArray.filter(f => 
          f.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name)
        );
        
        if (imageFiles.length === 0) {
          toast.error('No image files found');
          setProcessingFiles(false);
          return;
        }
        
        toast.info(`Processing ${imageFiles.length} photos...`);
      
      // Start with existing matches if in append mode
      const matches = appendMode ? [...photoMatches] : [];
      const teamMatches = appendMode ? [...teamPhotoMatches] : [];
      const unmatched = appendMode ? [...unmatchedPhotos] : [];
      
      // Track already-added files to prevent duplicates (same filename for same child/team)
      const seenKeys = new Set([
        ...matches.map(m => `${m.childName}|${m.file?.name}`),
        ...unmatched.map(m => `${m.childName}|${m.file?.name}`)
      ]);
      const seenTeamKeys = new Set(teamMatches.map(t => `${t.teamName}|${t.file?.name}`));
      let duplicatesSkipped = 0;
      
      // Separate team photos from individual photos
      const individualPhotos = [];
      const teamPhotos = [];
      
      for (const file of imageFiles) {
        try {
          const { isTeamPhoto, teamName } = extractTeamPhotoInfo(file.name);
          if (isTeamPhoto) {
            teamPhotos.push({ file, teamName });
          } else {
            individualPhotos.push(file);
          }
        } catch (err) {
          console.error('Error processing file:', file.name, err);
          individualPhotos.push(file);
        }
      }
      
      // Process team photos - NO previews to save memory
      for (const { file, teamName } of teamPhotos) {
        try {
          const teamKey = `${teamName}|${file.name}`;
          if (seenTeamKeys.has(teamKey)) {
            duplicatesSkipped++;
            continue;
          }
          seenTeamKeys.add(teamKey);
          const matchedPreorders = findPreordersByTeam(teamName);
          teamMatches.push({
            file,
            teamName,
            matchedPreorders,
            previewUrl: null // No preview - saves memory
          });
        } catch (err) {
          console.error('Error processing team photo:', file.name, err);
        }
      }
      
      // Group individual files by extracted child name
      const filesByChild = {};
      
      for (const file of individualPhotos) {
        try {
          const childName = extractChildName(file.name);
          if (!filesByChild[childName]) {
            filesByChild[childName] = [];
          }
          filesByChild[childName].push(file);
        } catch (err) {
          console.error('Error extracting child name:', file.name, err);
        }
      }
      
      // Match each group to preorders - NO previews to save memory
      for (const [childName, childFiles] of Object.entries(filesByChild)) {
        try {
          const { preorder, confidence } = findMatchingPreorder(childName);
          
          for (const file of childFiles) {
            const dupKey = `${childName}|${file.name}`;
            if (seenKeys.has(dupKey)) {
              duplicatesSkipped++;
              continue;
            }
            seenKeys.add(dupKey);
            const matchData = {
              file,
              childName,
              matchedPreorder: preorder,
              confidence,
              previewUrl: null // No preview - saves memory
            };
            
            if (preorder && confidence >= 50) {
              matches.push(matchData);
            } else {
              unmatched.push(matchData);
            }
          }
        } catch (err) {
          console.error('Error matching files for:', childName, err);
        }
      }
      
      setPhotoMatches(matches);
      setTeamPhotoMatches(teamMatches);
      setUnmatchedPhotos(unmatched);
      setProcessingFiles(false);
      setStep('review');
      
      const teamKidsCount = teamMatches.reduce((sum, t) => sum + t.matchedPreorders.length, 0);
      toast.success(
        `Matched ${matches.length} individual photos, ${teamMatches.length} team photos (${teamKidsCount} kids). ${unmatched.length} unmatched.`
      );
      if (duplicatesSkipped > 0) {
        toast.warning(`${duplicatesSkipped} duplicate file(s) skipped (same filename already added for that child/team)`);
      }
    } catch (error) {
      console.error('Error in handleFiles:', error);
      toast.error('Error processing files: ' + error.message);
      setProcessingFiles(false);
    }
    }, 100); // Small delay to allow UI to show loading state
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const items = e.dataTransfer.items;
    const collectedFiles = [];
    
    // Recursive function to read directory entries
    const readDirectory = (dirReader) => {
      return new Promise((resolve) => {
        const allEntries = [];
        
        const readBatch = () => {
          dirReader.readEntries((entries) => {
            if (entries.length === 0) {
              resolve(allEntries);
            } else {
              allEntries.push(...entries);
              readBatch(); // Continue reading (directories can have many files)
            }
          }, (error) => {
            console.error('Error reading directory:', error);
            resolve(allEntries);
          });
        };
        
        readBatch();
      });
    };
    
    // Process a single entry (file or directory)
    const processEntry = async (entry) => {
      try {
        if (entry.isFile) {
          return new Promise((resolve) => {
            entry.file((file) => {
              // Check if it's an image
              if (file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name)) {
                collectedFiles.push(file);
              }
              resolve();
            }, (error) => {
              console.error('Error getting file:', error);
              resolve();
            });
          });
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          const entries = await readDirectory(dirReader);
          
          // Process all entries in the directory
          for (const subEntry of entries) {
            await processEntry(subEntry);
          }
        }
      } catch (error) {
        console.error('Error processing entry:', error);
      }
    };
    
    const processItems = async () => {
      try {
        const entries = [];
        
        // Collect all entries first
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const entry = item.webkitGetAsEntry?.();
          if (entry) {
            entries.push(entry);
          } else if (item.kind === 'file') {
            const file = item.getAsFile();
            if (file && (file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name))) {
              collectedFiles.push(file);
            }
          }
        }
        
        // Process all entries
        for (const entry of entries) {
          await processEntry(entry);
        }
        
        if (collectedFiles.length > 0) {
          handleFiles(collectedFiles);
        } else {
          toast.error('No image files found in the dropped items');
        }
      } catch (error) {
        console.error('Error processing dropped items:', error);
        toast.error('Error processing files. Please try again.');
      }
    };
    
    processItems();
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  // Handle adding more files to existing batch (doesn't reset)
  const handleAddMoreFiles = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      // Snapshot the FileList BEFORE clearing the input (clearing empties the live FileList)
      const files = Array.from(e.target.files);
      e.target.value = '';
      handleFiles(files, true); // true = append mode
    } else {
      e.target.value = '';
    }
  };

  // Update match for a specific photo
  const updateMatch = (index, preorderId, isUnmatched = false) => {
    // Handle unassign value
    const actualPreorderId = preorderId === '__unassign__' ? null : preorderId;
    const preorder = actualPreorderId ? preorders.find(p => p.id === actualPreorderId) : null;
    
    if (isUnmatched) {
      const updated = [...unmatchedPhotos];
      updated[index] = { ...updated[index], matchedPreorder: preorder, confidence: preorder ? 100 : 0 };
      
      if (preorder) {
        // Move to matched
        setPhotoMatches([...photoMatches, updated[index]]);
        setUnmatchedPhotos(updated.filter((_, i) => i !== index));
      } else {
        setUnmatchedPhotos(updated);
      }
    } else {
      const updated = [...photoMatches];
      updated[index] = { ...updated[index], matchedPreorder: preorder, confidence: preorder ? 100 : 0 };
      
      if (!preorder) {
        // Move to unmatched
        setUnmatchedPhotos([...unmatchedPhotos, updated[index]]);
        setPhotoMatches(updated.filter((_, i) => i !== index));
      } else {
        setPhotoMatches(updated);
      }
    }
  };

  // Upload and assign all matched photos
  const uploadAndAssignAll = async () => {
    
    // Prevent double-clicks
    if (assigningAll) {
      return;
    }
    
    const totalIndividual = photoMatches.length;
    const totalTeamPhotos = teamPhotoMatches.length;
    const totalTeamAssignments = teamPhotoMatches.reduce((sum, t) => sum + t.matchedPreorders.length, 0);
    
    if (totalIndividual === 0 && totalTeamPhotos === 0) {
      toast.error('No matched photos to upload');
      return;
    }
    
    toast.info('Starting upload...');
    setAssigningAll(true);
    const totalOperations = totalIndividual + totalTeamAssignments;
    setUploadProgress({ current: 0, total: totalOperations });
    
    try {
      let uploaded = 0;
      let failed = 0;
      let dupSkipped = 0;
      const failedMatches = [];
      const failedTeamMatches = [];
      
      // 1. Upload individual photos - ONE PHOTO AT A TIME with compression
      toast.info('Compressing and uploading photos...');
      
      for (const match of photoMatches) {
        if (!match.matchedPreorder) {
          continue;
        }
        
        try {
          // Compress the image before uploading
          const compressedFile = await compressImage(match.file);
          
          const formData = new FormData();
          formData.append('files', compressedFile);
          formData.append('preorder_id', match.matchedPreorder.id);
          formData.append('event_id', eventId);
          
          const res = await postWithRetry(`${API}/preorders/upload-photos`, formData);
          dupSkipped += res.data?.skipped_duplicates || 0;
          
          uploaded++;
          setUploadProgress({ current: uploaded + failed, total: totalOperations });
        } catch (error) {
          console.error(`Failed to upload ${match.file?.name}:`, error);
          failed++;
          failedMatches.push(match);
          setUploadProgress({ current: uploaded + failed, total: totalOperations });
        }
      }
      
      // 2. Upload team photos - each team photo goes to ALL matching preorders
      for (const teamMatch of teamPhotoMatches) {
        if (teamMatch.matchedPreorders.length === 0) continue;
        
        try {
          // Compress the team photo before uploading
          const compressedFile = await compressImage(teamMatch.file);
          
          const formData = new FormData();
          formData.append('files', compressedFile);
          formData.append('event_id', eventId);
          formData.append('is_team_photo', 'true');
          
          // Send all preorder IDs that should get this team photo
          const preorderIds = teamMatch.matchedPreorders.map(p => p.id);
          formData.append('preorder_ids', JSON.stringify(preorderIds));
          
          const teamRes = await postWithRetry(`${API}/preorders/upload-team-photo`, formData);
          dupSkipped += teamRes.data?.skipped_duplicates || 0;
          
          uploaded += teamMatch.matchedPreorders.length;
          setUploadProgress({ current: uploaded + failed, total: totalOperations });
        } catch (error) {
          console.error(`Failed to upload team photo for ${teamMatch.teamName}:`, error);
          failed += teamMatch.matchedPreorders.length;
          failedTeamMatches.push(teamMatch);
        }
      }
      
      if (dupSkipped > 0) {
        toast.warning(`${dupSkipped} duplicate photo(s) were skipped (already assigned to that child)`);
      }
      if (failed > 0) {
        // Keep ONLY the failed photos in the list so "Upload & Assign All" retries just those
        setPhotoMatches(failedMatches);
        setTeamPhotoMatches(failedTeamMatches);
        toast.error(`${failed} photo(s) failed after retries. They are still listed - click "Upload & Assign All" to retry them.`);
        if (uploaded > 0) {
          toast.success(`${uploaded} photos uploaded successfully.`);
        }
      } else if (uploaded > 0) {
        toast.success(`Uploaded ${uploaded} photos successfully!`);
        setStep('complete');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photos: ' + error.message);
    } finally {
      setAssigningAll(false);
    }
  };

  // Filter matches based on search
  const filteredMatches = photoMatches.filter(m => {
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return m.childName.toLowerCase().includes(search) ||
           m.matchedPreorder?.child_name?.toLowerCase().includes(search);
  });

  const filteredUnmatched = unmatchedPhotos.filter(m => {
    if (!searchFilter) return true;
    return m.childName.toLowerCase().includes(searchFilter.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/preorder-events')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Upload Photos</h1>
          <p className="text-slate-600">{event?.name} - {preorders.length} pre-orders</p>
        </div>
      </div>

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <Card className="border-slate-200 relative">
          {/* Processing overlay */}
          {processingFiles && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
                <p className="text-lg font-medium text-slate-700">Processing photos...</p>
                <p className="text-sm text-slate-500">This may take a moment for large folders</p>
              </div>
            </div>
          )}
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <FolderUp className="h-5 w-5 text-amber-600" />
              Upload Event Photos
            </CardTitle>
            <CardDescription>
              Drag & drop your photo folders or select files. Photos will be automatically matched to pre-orders based on the child&apos;s name in the filename.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Hidden folder input */}
            <input
              type="file"
              ref={folderInputRef}
              multiple
              onChange={handleFileSelect}
              className="hidden"
              webkitdirectory=""
            />
            
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {/* Drop zone */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-xl p-12 text-center transition-all
                ${isDragging 
                  ? 'border-amber-500 bg-amber-50' 
                  : 'border-slate-300 hover:border-amber-400'
                }
              `}
            >
              <FolderUp className={`h-16 w-16 mx-auto mb-4 ${isDragging ? 'text-amber-500' : 'text-slate-400'}`} />
              <p className="text-lg font-medium text-slate-700 mb-2">
                {isDragging ? 'Drop your photos here!' : 'Drag & drop photo folders here'}
              </p>
              <p className="text-slate-500 mb-4">
                or use the buttons below
              </p>
              
              {/* Buttons for folder/file selection */}
              <div className="flex items-center justify-center gap-4 mb-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
                  className="bg-white"
                >
                  <FolderUp className="h-4 w-4 mr-2" />
                  Select Folder
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="bg-white"
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Select Files
                </Button>
              </div>
              
              <div className="text-sm text-slate-400 space-y-1">
                <p>Supported: JPG, JPEG, PNG, WebP</p>
                <p>Individual: <code className="bg-slate-100 px-2 py-1 rounded">Child Name-01.jpg</code></p>
                <p>Team photo: <code className="bg-slate-100 px-2 py-1 rounded">Team Name - Team Photo.jpg</code></p>
              </div>
            </div>

            {/* Quick tips */}
            <div className="mt-6 bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li><strong>Individual photos:</strong> Name like &quot;Dalton Fletcher-01.jpg&quot; → matches to that child</li>
                <li><strong>Team photos:</strong> Name like &quot;Glitter Goals - Team Photo.jpg&quot; → assigned to ALL kids on that team</li>
                <li>Review the matches and fix any that didn&apos;t match correctly</li>
                <li>Click &quot;Upload &amp; Assign All&quot; to save everything at once</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manage Uploaded Team Photos */}
      {step === 'upload' && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Manage Uploaded Team Photos
                </CardTitle>
                <CardDescription>Fix misassigned team photos - remove one from a whole team (or everyone) in one click</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchTeamPhotoManager} disabled={loadingTeamPhotos} data-testid="load-team-photos-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingTeamPhotos ? 'animate-spin' : ''}`} />
                {managedTeamPhotos === null ? 'Show Team Photos' : 'Refresh'}
              </Button>
            </div>
          </CardHeader>
          {managedTeamPhotos !== null && (
            <CardContent>
              {managedTeamPhotos.length === 0 ? (
                <p className="text-sm text-slate-500">No team photos uploaded for this event yet.</p>
              ) : (
                <div className="space-y-3">
                  {managedTeamPhotos.map((tp) => (
                    <div key={tp.photo_id} className="flex gap-4 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`managed-team-photo-${tp.photo_id}`}>
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${tp.thumbnail_url || tp.url}`}
                        alt={tp.filename}
                        className="w-20 h-20 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{tp.filename}</p>
                        <p className="text-sm text-slate-500 mb-2">
                          Assigned to {tp.assigned_count} pre-order{tp.assigned_count === 1 ? '' : 's'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(tp.teams).map(([team, count]) => (
                            <Button
                              key={team}
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                              disabled={removingTeamPhoto === tp.photo_id}
                              onClick={() => removeTeamPhotoAssignment(tp.photo_id, [team], `${team} (${count} kids)`)}
                              data-testid={`remove-from-team-${tp.photo_id}-${team}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Remove from {team} ({count})
                            </Button>
                          ))}
                          {tp.assigned_count > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs border-red-400 text-red-700 hover:bg-red-50"
                              disabled={removingTeamPhoto === tp.photo_id}
                              onClick={() => removeTeamPhotoAssignment(tp.photo_id, null, `ALL ${tp.assigned_count} pre-orders`)}
                              data-testid={`remove-from-all-${tp.photo_id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Remove from all
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Step 2: Review Matches */}
      {step === 'review' && (
        <>
          {/* Hidden input for adding more photos */}
          <input
            type="file"
            ref={addMoreInputRef}
            multiple
            accept=".jpg,.jpeg,.png,.gif,.webp,image/*"
            onChange={handleAddMoreFiles}
            className="hidden"
          />
          
          {/* Summary Bar */}
          <Card className="border-slate-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="font-medium">{photoMatches.length} Individual</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span className="font-medium">
                      {teamPhotoMatches.length} Team Photos 
                      ({teamPhotoMatches.reduce((sum, t) => sum + t.matchedPreorders.length, 0)} kids)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-orange-500" />
                    <span className="font-medium">{unmatchedPhotos.length} Unmatched</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => { setPhotoMatches([]); setTeamPhotoMatches([]); setUnmatchedPhotos([]); setStep('upload'); }}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Start Over
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => addMoreInputRef.current?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Add More Photos
                  </Button>
                  <Button 
                    onClick={uploadAndAssignAll}
                    disabled={(photoMatches.length === 0 && teamPhotoMatches.length === 0) || assigningAll}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {assigningAll ? (
                      <>Uploading {uploadProgress.current}/{uploadProgress.total}...</>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Upload & Assign All
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              {assigningAll && (
                <Progress value={(uploadProgress.current / uploadProgress.total) * 100} className="mt-3" />
              )}
            </CardContent>
          </Card>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Team Photos */}
          {teamPhotoMatches.length > 0 && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                  <Users className="h-5 w-5" />
                  Team Photos ({teamPhotoMatches.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {teamPhotoMatches.map((match, index) => (
                    <div key={index} className="bg-white rounded-lg border border-blue-200 p-4">
                      <div className="flex gap-4">
                        {match.previewUrl ? (
                          <img 
                            src={match.previewUrl} 
                            alt={match.teamName}
                            className="w-24 h-24 object-cover rounded"
                          />
                        ) : (
                          <div className="w-24 h-24 bg-slate-100 rounded flex items-center justify-center">
                            <ImageIcon className="h-8 w-8 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{match.file.name}</p>
                          <p className="text-sm text-blue-700 mb-2">
                            Team: <strong>{match.teamName}</strong>
                          </p>
                          {match.matchedPreorders.length > 0 ? (
                            <div>
                              <p className="text-sm text-green-700 mb-1">
                                ✓ Will be assigned to {match.matchedPreorders.length} kids (click ✕ to remove):
                              </p>
                              <div className="flex flex-wrap gap-1 mb-2">
                                {match.matchedPreorders.map((p) => (
                                  <Badge key={p.id} variant="outline" className="text-xs bg-green-50 pr-1 gap-1" data-testid={`team-match-kid-${p.id}`}>
                                    {p.child_name}
                                    <button
                                      type="button"
                                      onClick={() => removeFromTeamMatch(index, p.id)}
                                      className="hover:bg-red-100 rounded-full p-0.5"
                                      data-testid={`remove-team-match-${p.id}`}
                                    >
                                      <X className="h-3 w-3 text-red-500" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-orange-600 mb-2">
                              ⚠ No matching team found. Check team name spelling, or add kids below.
                            </p>
                          )}
                          <PreorderCombobox
                            value={null}
                            onValueChange={(pid) => addToTeamMatch(index, pid)}
                            preordersByTeam={preordersByTeam}
                            placeholder="+ Add a child to this team photo..."
                            className="max-w-xs"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Matched Photos */}
          {filteredMatches.length > 0 && (
            <Card className="border-green-200 bg-green-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                  <CheckCircle2 className="h-5 w-5" />
                  Individual Photos ({filteredMatches.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredMatches.map((match, index) => (
                    <div key={index} className="bg-white rounded-lg border border-green-200 p-3 flex gap-3">
                      {match.previewUrl ? (
                        <img 
                          src={match.previewUrl} 
                          alt={match.childName}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                          <ImageIcon className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate" title={match.file.name}>
                          {match.file.name}
                        </p>
                        <p className="text-xs text-slate-500 mb-1">→ {match.childName}</p>
                        <PreorderCombobox
                          value={match.matchedPreorder?.id || ''}
                          onValueChange={(val) => updateMatch(index, val, false)}
                          preordersByTeam={preordersByTeam}
                          placeholder="Select child..."
                        />
                      </div>
                      <Badge variant="outline" className="h-fit bg-green-100 text-green-700 border-green-300">
                        {match.confidence}%
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Unmatched Photos */}
          {filteredUnmatched.length > 0 && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
                  <AlertCircle className="h-5 w-5" />
                  Unmatched Photos ({filteredUnmatched.length})
                  <span className="text-sm font-normal text-orange-600">- Assign manually</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredUnmatched.map((match, index) => (
                    <div key={index} className="bg-white rounded-lg border border-orange-200 p-3 flex gap-3">
                      {match.previewUrl ? (
                        <img 
                          src={match.previewUrl} 
                          alt={match.childName}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                          <ImageIcon className="h-6 w-6 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate" title={match.file.name}>
                          {match.file.name}
                        </p>
                        <p className="text-xs text-slate-500 mb-1">→ {match.childName}</p>
                        <PreorderCombobox
                          value={match.matchedPreorder?.id || ''}
                          onValueChange={(val) => updateMatch(index, val, true)}
                          preordersByTeam={preordersByTeam}
                          placeholder="Select child..."
                          className="border-orange-300"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Step 3: Complete */}
      {step === 'complete' && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-green-800 mb-2">Photos Uploaded!</h2>
            <p className="text-green-700 mb-6">
              All matched photos have been uploaded and assigned to their pre-orders.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button variant="outline" onClick={() => { setPhotoMatches([]); setUnmatchedPhotos([]); setStep('upload'); }}>
                Upload More Photos
              </Button>
              <Button onClick={() => navigate('/admin/preorders')} className="bg-green-600 hover:bg-green-700">
                View Pre-Orders
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Wrap with Error Boundary
const EventPhotoUploadPage = () => (
  <ErrorBoundary>
    <EventPhotoUploadPageInner />
  </ErrorBoundary>
);

export default EventPhotoUploadPage;
